import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useEditorStore, type PlanViewInsets } from '@repo/front-end-state';

/**
 * What a glass step lays over its plan, for the fly-to (`plan-fly-to`, R3; the planning ruling of
 * 2026-11-03: everything over the plan, on both steps). Each covering piece carries
 * `data-plan-cover="{edge}"` — the nav bar `top`, the step list and the list panel `left` — and the
 * plan card counts on the `right` while it is open at its home corner. A card the operator moved
 * away is not an inset.
 */
export const PLAN_COVER_ATTR = 'data-plan-cover';

export type PlanCoverEdge = 'top' | 'right' | 'bottom' | 'left';

/** How long after a fly-to is asked for the covers are measured each frame: the flight and a margin. */
const MEASURE_FOR_MS = 900;

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * The insets `covers` lay over `pane`, in pane pixels: each cover reaches in from its edge as far as
 * its far side. A cover that does not overlap the pane, or has no size, counts for nothing.
 */
export function coverInsets(
  pane: Box,
  covers: ReadonlyArray<{ edge: PlanCoverEdge; box: Box }>,
): PlanViewInsets {
  const insets: PlanViewInsets = { top: 0, right: 0, bottom: 0, left: 0 };
  for (const { edge, box } of covers) {
    const overlaps =
      box.right > pane.left &&
      box.left < pane.right &&
      box.bottom > pane.top &&
      box.top < pane.bottom &&
      box.right > box.left &&
      box.bottom > box.top;
    if (!overlaps) continue;
    const reach = {
      top: box.bottom - pane.top,
      right: pane.right - box.left,
      bottom: pane.bottom - box.top,
      left: box.right - pane.left,
    }[edge];
    insets[edge] = Math.max(insets[edge], reach);
  }
  return insets;
}

const EDGES: readonly PlanCoverEdge[] = ['top', 'right', 'bottom', 'left'];
const asEdge = (value: string | null): PlanCoverEdge | null =>
  (EDGES as readonly string[]).includes(value ?? '') ? (value as PlanCoverEdge) : null;

const sameInsets = (a: PlanViewInsets | null, b: PlanViewInsets): boolean =>
  !!a && a.top === b.top && a.right === b.right && a.bottom === b.bottom && a.left === b.left;

/**
 * Keep the editor store's `planViewInsets` true to what covers `paneRef` while the step is mounted:
 * measured when a fly-to is asked for and on every frame of its flight, so a card that opens
 * mid-flight moves the landing point (R3.3). `cardCovers()` says whether the plan card is open at
 * its home corner now. Cleared on unmount, so a step without covers lands in the pane's middle.
 */
export function usePlanCoverInsets(
  paneRef: RefObject<HTMLElement | null>,
  cardCovers: () => boolean,
): void {
  const cardCoversRef = useRef(cardCovers);
  cardCoversRef.current = cardCovers;
  useEffect(() => {
    const measure = () => {
      const pane = paneRef.current;
      if (!pane) return;
      const covers: Array<{ edge: PlanCoverEdge; box: Box }> = [];
      document.querySelectorAll(`[${PLAN_COVER_ATTR}]`).forEach(el => {
        const edge = asEdge(el.getAttribute(PLAN_COVER_ATTR));
        if (edge) covers.push({ edge, box: el.getBoundingClientRect() });
      });
      if (cardCoversRef.current()) {
        pane.querySelectorAll('[data-movable-card]').forEach(el => {
          covers.push({ edge: 'right', box: el.getBoundingClientRect() });
        });
      }
      const next = coverInsets(pane.getBoundingClientRect(), covers);
      const store = useEditorStore.getState();
      if (!sameInsets(store.planViewInsets, next)) store.setPlanViewInsets(next);
    };
    let frame: number | null = null;
    let until = 0;
    const loop = () => {
      measure();
      frame = performance.now() < until ? requestAnimationFrame(loop) : null;
    };
    measure();
    const unsubscribe = useEditorStore.subscribe(
      s => s.flyRequest,
      request => {
        if (!request) return;
        until = performance.now() + MEASURE_FOR_MS;
        measure();
        if (frame === null) frame = requestAnimationFrame(loop);
      },
    );
    return () => {
      unsubscribe();
      if (frame !== null) cancelAnimationFrame(frame);
      useEditorStore.getState().setPlanViewInsets(null);
    };
  }, [paneRef]);
}
